<template>
    <!--
        TODO:
        - [ ] Improvement: Add possibility to sort the data based on the column.
        - [ ] Improvement: Add possibility to pass the date (period) in the url. (Set to current month if future date is passed.)
        - [ ] Improvement: Show historical data for current entity.
    -->
    <page-wrapper>
        <page-header>
            <template v-slot:breadcrumbs>
                <q-breadcrumbs-el :label="$tc('common.commission.commission', 2)" :to="{ name: 'commissions-reporting' }" />
                <q-breadcrumbs-el v-if="isLoadingEntity">
                    <skeleton type="text" />
                </q-breadcrumbs-el>
                <template v-if="entity && entity.consultingSettings.company">
                    <q-breadcrumbs-el
                        v-if="entity.consultingSettings.company.id !== $store.state?.user?.tenantCompany?.id"
                        label="…"
                    />
                    <q-breadcrumbs-el
                        :label="entity.consultingSettings.company.getContactName()"
                        :to="{ name: 'commission-turnover-and-income-report', params: { id: entity.consultingSettings.company.id }}"
                    />
                </template>
                <q-breadcrumbs-el v-if="entity">{{ entity.getContactName() }}</q-breadcrumbs-el>
            </template>

            <!--region # Entity header-->
            <h1>{{ $tc('common.reporting.commission-turnover-and-income.report-title', 1) }} {{ $store.state.user.tenantCompany.company_name }} – {{ formattedReportDateMonth }}
                <br>
                <small class="subtitle">
                    <skeleton
                        v-if="isLoadingEntity || isLoadingEntityReports"
                        type="text"
                        style="width: 18ch;"
                        class="inline-block"
                    />
                    <template v-else>
                        <template v-if="currentEntityReport">{{ $tc('common.term.from-x-to-y--short', 1, { x: formatDate(currentEntityReport.metadata.start_date), y: formatDate(currentEntityReport.metadata.end_date) }) }}
                            <!-- TODO: Add condition to only show this badge when there are unlocked postings (after locking postings/posting periods has been introduced) -->
                            <!-- TODO: Apply same condition to info-box at bottom once implemented -->
                            <status-badge
                                :label="$tc('common.reporting.accounting.unlocked-posting', 2)"
                                status="warning"
                                :info-title="$tc('common.reporting.accounting.period-contains-unlocked-postings', 1)"
                                :info-text="$tc('common.reporting.accounting.unlocked-posting--info', 1)"
                            />
                        </template>
                        <template v-else>{{ $tc('common.reporting.no-data-for-period', 1) }}</template>
                    </template>
                </small>
            </h1>
            <!--endregion-->

            <!--region # Report date navigation-->
            <div class="q-mt-sm q-mb-xs q-gutter-xs do-not-print">
                <q-btn-group flat>
                    <base-button
                        color="secondary-lighter"
                        text-color="text-primary"
                        @click="setReportDateToPreviousMonth"
                    >
                        <q-icon name="mib-arrow-left-1" size="0.75em" class="q-mr-sm" />
                        {{ $tc('common.time.month--previous-n-months', 1) }}
                    </base-button>
                    <base-button
                        color="secondary-lighter"
                        text-color="text-primary"
                        :disabled="isReportDateInCurrentMonth"
                        @click="setReportDateToNextMonth"
                    >
                        {{ $tc('common.time.month--next-n-months', 1) }}
                        <q-icon name="mib-arrow-right-1" size="0.75em" class="q-ml-sm" />
                    </base-button>
                </q-btn-group>

                <base-button
                    :label="$tc('common.time.month--current-month', 1)"
                    color="secondary-lighter"
                    text-color="text-primary"
                    icon="mib-calendar"
                    :disabled="isReportDateInCurrentMonth"
                    @click="setReportDateToCurrentMonth"
                />
            </div>
            <!--endregion-->
        </page-header>

        <!--region # Entity-->
        <div class="row">
            <div class="col-xs-12 col-lg-8 offset-lg-2">
                <div class="row print-row-md">
                    <div class="col-xs-12 q-mt-xl q-mb-lg text-center">
                        <h2 class="text-xl text-normal">
                            <skeleton v-if="isLoadingEntity || isLoadingEntityReports" type="text" class="inline-block" />
                            <template v-else>
                                <template v-if="entity">{{ entity.getContactName() }}</template>
                            </template>
                        </h2>

                        <div class="do-not-print">
                            <skeleton v-if="isLoadingEntity || isLoadingEntityReports" type="text" class="inline-block" />
                            <template v-else>
                                <template v-if="entity">
                                    <router-link
                                        v-if="entity?.consultingSettings?.company"
                                        :to="{ name: 'commission-turnover-and-income-report', params: { id: entity.consultingSettings.company.id }}"
                                    ><q-icon name="mib-arrow-left" size="0.75em" class="q-mr-sm" />{{ entity.consultingSettings.company.getContactName() }}</router-link>
                                    <status-badge v-if="!entity.consultingSettings?.company" status="system-owner" />
                                </template>
                            </template>
                        </div>
                    </div>

                    <div class="col-xs-12 col-md-6 q-pa-sm text-center">
                        <div class="q-px-md q-pt-lg q-pb-md rounded-borders entity-report-amount-wrapper">
                            <span class="text-xl entity-report-amount turnover-amount">
                                <skeleton
                                    v-if="isLoadingEntity || isLoadingEntityReports"
                                    type="text"
                                    class="inline-block"
                                    style="width: 8ch;"
                                />
                                <span v-else :class="Number(currentEntityReport?.data?.turnover_amount) < 0 ? 'text-negative-dark' : 'text-text-primary'">{{ currentEntityReport?.data?.turnover_amount ? formatCurrency(currentEntityReport.data.turnover_amount) : '–' }}</span>
                            </span>
                            <p class="text-lg text-text-secondary q-mb-none entity-report-amount-label turnover-label">{{ $tc('common.reporting.commission-turnover-and-income.commission-turnover', 1) }}</p>
                        </div>
                    </div>

                    <div class="col-xs-12 col-md-6 q-pa-sm text-center">
                        <div class="q-px-md q-pt-lg q-pb-md rounded-borders entity-report-amount-wrapper">
                            <span class="text-xl entity-report-amount revenue">
                                <skeleton
                                    v-if="isLoadingEntity || isLoadingEntityReports"
                                    type="text"
                                    class="inline-block"
                                    style="width: 8ch;"
                                />
                                <span v-else :class="Number(currentEntityReport?.data?.revenue_amount < 0) ? 'text-negative-dark' : 'text-text-primary'">{{ currentEntityReport?.data?.revenue_amount ? formatCurrency(currentEntityReport.data.revenue_amount) : '–' }}</span>
                            </span>
                            <p class="text-lg text-text-secondary q-mb-none entity-report-amount-label revenue-label">{{ $tc('common.reporting.commission-turnover-and-income.commission-income', 1) }}</p>
                        </div>
                    </div>
                </div>

                <p class="additional-info text-center q-mt-md q-mb-none">
                    <skeleton
                        v-if="isLoadingEntity || isLoadingEntityReports"
                        type="text"
                        class="inline-block"
                        style="width: 40ch;"
                    />
                    <template v-else-if="entity && entity.consultingSettings.contact_id">{{ $tc(`common.reporting.commission-turnover-and-income.commission-turnover-and-income${entity.consultingSettings.contact_id === $store.state?.user?.tenantCompany?.id ? '--for-x' : '--generated-by-x--for-y'}`, 1, { reportForEntity: $store.state.user.tenantCompany.company_name, entityName: entity.getContactName() }) }}</template>
                </p>
            </div>
        </div>
        <!--endregion-->

        <!--region # No subentities && amount(s) = 0-->
        <info-box v-if="!(isLoadingEntity || isLoadingEntityReports || isLoadingSubentities || isLoadingSubentityReports) && subentities.length === 0 && (Number(currentEntityReport?.data?.turnover_amount) === 0 || Number(currentEntityReport?.data?.revenue_amount) === 0)">
            <p>{{ $t('common.reporting.commission-turnover-and-income.no-subentities--amount-zero--info') }}</p>
        </info-box>

        <!--region # Subentities-->
        <div :class="['row q-mt-xl subentities-wrapper', { 'has-no-subentities': !(isLoadingEntity || isLoadingEntityReports || isLoadingSubentities || isLoadingSubentityReports) && subentities.length === 0 }]">
            <div class="col-xs-12">
                <hr class="q-mb-xl">
                <h2 class="text-h4">{{ $tc('common.reporting.commission-turnover-and-income.subordinate-consulting-entities', 1) }}</h2>

                <info-box class="do-not-print">
                    <p v-if="$store.state.user.tenantCompany.company_name && entity">{{ $tc('common.reporting.commission-turnover-and-income.amount-sum--info--entity-name--report-for-entity', 1, { reportForEntity: $store.state.user.tenantCompany.company_name, entityName: entity.getContactName() }) }}</p>
                    <p v-else-if="$store.state.user.tenantCompany.company_name && !entity">{{ $tc('common.reporting.commission-turnover-and-income.amount-sum--info--report-for-entity', 1, { reportForEntity: $store.state.user.tenantCompany.company_name }) }}</p>
                    <p v-else-if="!$store.state.user.tenantCompany.company_name && entity">{{ $tc('common.reporting.commission-turnover-and-income.amount-sum--info--entity-name', 1, { entityName: entity.getContactName() }) }}</p>
                    <p v-else>{{ $tc('common.reporting.commission-turnover-and-income.amount-sum--info', 1) }}</p>
                </info-box>

                <template>
                    <base-checkbox v-model="showInactiveSubentities" :label="$tc('common.reporting.commission-turnover-and-income.include-inactive-consulting-entities', 2)" class="q-mr-xs" />
                    <base-checkbox v-model="hideZeroAmountSubentities" :label="$tc('common.reporting.commission-turnover-and-income.hide-zero-amount-subentities', 1)" class="q-mr-xs" />
                    <q-table
                        :data="subentitiesItems"
                        :columns="subentitiesTableColumns"
                        :rows-per-page-options="[0]"
                        row-key="id"
                        flat
                        square
                        dense
                        :loading="isLoadingSubentities || isLoadingSubentityReports"
                        @row-dblclick="(evt, row) => $router.push({ name: 'commission-turnover-and-income-report', params: { id: row.id } })"
                    >
                        <template v-slot:body-cell-name="slotProps">
                            <q-td :props="slotProps">
                                <q-icon
                                    :name="slotProps.row.type === ContactType.COMPANY ? 'mib-building-1' : 'mib-single-neutral'"
                                    color="border-tertiary"
                                    class="q-mr-xs"
                                    data-test="contact-name-type-icon"
                                />
                                <span data-test="text:subentity-name">{{ slotProps.row.name }}</span>
                                <status-badge v-if="slotProps.row.consulting_settings_status && slotProps.row.consulting_settings_status !== ConsultingSettingsStatus.ACTIVE" :status="slotProps.row.consulting_settings_status" />
                            </q-td>
                        </template>

                        <template v-slot:body-cell-turnover_amount="slotProps">
                            <q-td :props="slotProps">
                                <skeleton
                                    v-if="isLoadingSubentityReports"
                                    type="text"
                                    class="inline-block"
                                    style="width: 12ch;"
                                />
                                <span v-else>{{ slotProps.row.turnover_amount ? formatCurrency(slotProps.row.turnover_amount) : '-' }}</span>
                            </q-td>
                        </template>

                        <template v-slot:body-cell-revenue_amount="slotProps">
                            <q-td :props="slotProps">
                                <skeleton
                                    v-if="isLoadingSubentityReports"
                                    type="text"
                                    class="inline-block"
                                    style="width: 12ch;"
                                />
                                <span v-else>{{ slotProps.row.revenue_amount ? formatCurrency(slotProps.row.revenue_amount) : '-' }}</span>
                            </q-td>
                        </template>

                        <template v-slot:body-cell-actions="slotProps">
                            <q-td :props="slotProps">
                                <q-btn
                                    color="primary"
                                    size="sm"
                                    flat
                                    round
                                    icon="mib-view-1"
                                    :to="{ name: 'commission-turnover-and-income-report', params: { id: slotProps.row.id } }"
                                    data-test="btn:tooltip-details"
                                >
                                    <q-tooltip
                                        :delay="1000"
                                        :offset="[0, 10]"
                                    >
                                        {{ $tc('common.reporting.commission-turnover-and-income.show-commission-report-of-x', 1, { entityName: slotProps.row.name }) }}
                                    </q-tooltip>
                                </q-btn>
                            </q-td>
                        </template>

                        <template v-slot:bottom>
                            <span class="additional-info q-mt-xs print-only"><q-icon name="mib-information-circle" color="secondary-light" class="q-mr-xs" /> {{ $tc(`common.reporting.commission-turnover-and-income.amount-sum--info${entity? '--entity-name' : null}`, 1, { entityName: entity?.getContactName() }) }}</span>
                        </template>
                    </q-table>
                </template>
            </div>
        </div>
        <!--endregion-->

        <!-- TODO: Add condition to only show this badge when there are unlocked postings (after locking postings/posting periods has been introduced) -->
        <info-box type="secondary">
            <p><strong>{{ $tc('common.reporting.accounting.period-contains-unlocked-postings', 1) }}</strong></p>
            <p>{{ $tc('common.reporting.accounting.unlocked-posting--info', 1) }}</p>
            <p class="additional-info text-xxs">{{ lightFormat(new Date(), 'dd.MM.yyyy – HH:mm') }}</p>
        </info-box>
    </page-wrapper>
</template>

<script>
import BaseCheckbox from '@/components/form/BaseCheckbox.vue'
import { useI18n } from '@/composables/i18n'
import { i18n } from '@/i18n'
import { sortByContactName, sortByContactType } from '@/helpers/contact'
import { formatDate, getDateFnsLocale } from '@/helpers/date'
import { formatCurrency } from '@/helpers/number'
import { ContactService } from '@/services'
import { addMonths, endOfMonth, format, isSameMonth, lightFormat, startOfMonth, subMonths } from 'date-fns'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { ConsultingSettingsStatus, ContactType, GlobalReportType, MetadataFilterOperator } from '@/enums/graphql'
import { GlobalReport } from '@/models/globalReport'
import StatusBadge from '@/components/StatusBadge.vue'
import { EventBus } from '@/event-bus'
import { useRouter } from '@/composables/router'

export default {
    name: 'CommissionTurnoverAndRevenueReport',
    components: { StatusBadge, BaseCheckbox },
    props: {
        id: {
            type: String,
            required: true,
        },
    },
    methods: { lightFormat },
    meta () {
        return {
            title: `${this.$tc('common.reporting.commission-turnover-and-income.report-title', 1)} ${this.$store.state.user.tenantCompany.company_name} – ${this.formattedReportDateYearMonth}${(this.entity && this.entity?.id !== this.$store.state?.user?.tenantCompany?.id) ? ' – ' + this.entity.getContactName() : '' }`,
        }
    },
    setup (props) {
        const { tc } = useI18n()
        const router = useRouter()

        //region # Report date
        const reportDateUntil = ref(new Date())
        const reportDateFrom = computed(() => startOfMonth(reportDateUntil.value))
        const isReportDateInCurrentMonth = computed(() => {
            return isSameMonth(reportDateUntil.value, new Date())
        })
        const formattedReportDateMonth = computed(() => {
            return format(reportDateUntil.value, 'MMMM yyyy', { locale: getDateFnsLocale(i18n.locale) })
        })
        const formattedReportDateYearMonth = computed(() => {
            return format(reportDateUntil.value, 'yyyy-MM', { locale: getDateFnsLocale(i18n.locale) })
        })
        function setReportDateToPreviousMonth() {
            reportDateUntil.value = endOfMonth(subMonths(reportDateUntil.value, 1))
        }
        function setReportDateToNextMonth() {
            reportDateUntil.value = endOfMonth(addMonths(reportDateUntil.value, 1))
        }
        function setReportDateToCurrentMonth() {
            reportDateUntil.value = new Date()
        }
        function triggerSetReportDateToPreviousMonth() {
            setReportDateToPreviousMonth()
        }
        function triggerSetReportDateToNextMonth() {
            if (!isReportDateInCurrentMonth.value) {
                setReportDateToNextMonth()
            }
        }
        function triggerSetReportDateToCurrentMonth() {
            if (!isReportDateInCurrentMonth.value) {
                setReportDateToCurrentMonth()
            }
        }
        function triggerGoToSuperordinateEntity() {
            if (entity.value?.consultingSettings?.company?.id) {
                router.push({ name: 'commission-turnover-and-income-report', params: { id: entity.value.consultingSettings.company.id } })
            }
        }
        //endregion

        //region # Entity
        const isLoadingEntity = ref(false)
        const entity = ref(null)
        let entityReportsAbortController = null
        const isLoadingEntityReports = ref(false)
        const entityReports = ref([])
        const currentEntityReport = computed(() => {
            return entityReports.value.at(0)
        })
        function clearEntity () {
            entity.value = null
        }
        async function loadEntity () {
            isLoadingEntity.value = true
            const contact = await ContactService.get(props.id)
            entity.value = contact
            isLoadingEntity.value = false
        }
        function clearEntityReports () {
            entityReports.value = []
        }
        async function loadEntityReports () {
            if (entityReportsAbortController) entityReportsAbortController.abort()
            isLoadingEntityReports.value = true
            entityReportsAbortController = new AbortController()
            const reports = await GlobalReport.objects.all({
                search: {
                    type: GlobalReportType.COMMISSION_TURNOVER_AND_REVENUE,
                    metadata: [{ key: 'consulting_entity_id', value: props.id, operator: MetadataFilterOperator.EQUALS }],
                    report_date_from: lightFormat(reportDateFrom.value, 'yyyy-MM-dd'),
                    report_date_until: lightFormat(reportDateUntil.value, 'yyyy-MM-dd'),
                },
            }, { abortController: entityReportsAbortController })
            entityReports.value = reports
            isLoadingEntityReports.value = false
        }
        //endregion

        //region # Subentities
        const subentitiesTableColumns = [
            {
                name: 'name',
                label: tc('common.term.name'),
                align: 'left',
            },
            {
                name: 'turnover_amount',
                label: tc('common.reporting.commission-turnover-and-income.commission-turnover', 1),
                classes: row => `amount-column turnover-amount text-bold monospace ${(!row.turnover_amount || Number(row.turnover_amount) === 0) ? 'text-secondary' : null} ${Number(row.turnover_amount) < 0 ? 'text-negative-darker' : null}`,
                align: 'right',
            },
            {
                name: 'revenue_amount',
                label: tc('common.reporting.commission-turnover-and-income.commission-income', 1),
                classes: row => `amount-column revenue-amount text-bold monospace ${(!row.revenue_amount || Number(row.revenue_amount) === 0) ? 'text-secondary' : null} ${Number(row.revenue_amount) < 0 ? 'text-negative-darker' : null}`,
                align: 'right',
            },
            { name: 'actions', label: tc('common.term.action', 2), align: 'right', classes: 'actions-column', headerClasses: 'actions-column' },
        ]
        const showInactiveSubentities = ref(false)
        const hideZeroAmountSubentities = ref(false)
        const isLoadingSubentities = ref(false)
        const subentities = ref([])
        const isLoadingSubentityReports = ref(false)
        let subentityReportsAbortController = null
        const subentityReports = ref({})
        const subentitiesItems = computed(() => {
            return subentities.value.map(subentity => {
                const report = subentityReports.value[subentity.id]
                return {
                    id: subentity.id,
                    name: subentity.getContactName(),
                    type: subentity.type,
                    consulting_settings_status: subentity.consultingSettings.status,
                    turnover_amount: report?.turnover_amount,
                    revenue_amount: report?.revenue_amount,
                }
            }).filter(subentityItem => {
                if (showInactiveSubentities.value) {
                    return true
                }
                return subentityItem.consulting_settings_status === 'ACTIVE'
            }).filter(subentityItem => {
                if (!hideZeroAmountSubentities.value) {
                    return true
                }
                return !(
                    (subentityItem.turnover_amount === '0.00' || subentityItem.turnover_amount === undefined) &&
                    (subentityItem.revenue_amount === '0.00' || subentityItem.revenue_amount === undefined)
                )
            })
        })
        function clearSubentities () {
            subentities.value = []
        }
        async function loadSubentities () {
            isLoadingSubentities.value = true
            const response = await ContactService.all({ hasConsultingSettings: true, consultingCompanyIds: [props.id] }, `
                id
                consultingSettings {
                    status
                }
                ... on Person {
                    first_name
                    last_name
                    gender
                }
                ... on Company {
                    company_name
                }
            `)
            const contacts = response.data
            contacts.sort(sortByContactName)
            contacts.sort(sortByContactType)
            subentities.value = contacts
            isLoadingSubentities.value = false
        }
        function clearSubentityReports () {
            subentityReports.value = {}
        }
        async function loadSubentityReports () {
            if (subentityReportsAbortController) subentityReportsAbortController.abort()
            isLoadingSubentityReports.value = true
            subentityReportsAbortController = new AbortController()
            const reports = await GlobalReport.objects.all({
                search: {
                    type: GlobalReportType.COMMISSION_TURNOVER_AND_REVENUE,
                    metadata: [{ key: 'consulting_entity_id', value: subentities.value.map(contact => contact.id), operator: MetadataFilterOperator.CONTAINS }],
                    report_date_from: lightFormat(reportDateFrom.value, 'yyyy-MM-dd'),
                    report_date_until: lightFormat(reportDateUntil.value, 'yyyy-MM-dd'),
                },
            }, { abortController: subentityReportsAbortController })

            const newReports = {}
            reports.forEach(report => {
                newReports[report.metadata.consulting_entity_id] = Object.assign(report.metadata, report.data)
            })
            subentityReports.value = newReports

            isLoadingSubentityReports.value = false
        }
        //endregion

        // Watchers
        watch(() => props.id, async () => {
            clearEntity()
            clearEntityReports()
            clearSubentities()
            clearSubentityReports()

            await loadEntity()
            await loadEntityReports()

            if (entity.value.type === ContactType.COMPANY) {
                await loadSubentities()
                await loadSubentityReports()
            }
        }, { immediate: true })
        watch(() => reportDateUntil.value, async () => {
            clearEntityReports()
            isLoadingSubentityReports.value = true

            await loadEntityReports()
            if (entity.value.type === ContactType.COMPANY) {
                await loadSubentityReports()
            } else {
                clearSubentityReports()
                isLoadingSubentityReports.value = false
            }
        })

        // Hooks
        onMounted(() => {
            EventBus.$on('shortcut:left', triggerSetReportDateToPreviousMonth)
            EventBus.$on('shortcut:right', triggerSetReportDateToNextMonth)
            EventBus.$on('shortcut:today', triggerSetReportDateToCurrentMonth)
            EventBus.$on('shortcut:up', triggerGoToSuperordinateEntity)
        })

        onUnmounted(() => {
            EventBus.$off('shortcut:left', triggerSetReportDateToPreviousMonth)
            EventBus.$off('shortcut:right', triggerSetReportDateToNextMonth)
            EventBus.$off('shortcut:today', triggerSetReportDateToCurrentMonth)
            EventBus.$off('shortcut:up', triggerGoToSuperordinateEntity)
        })
        return {
            // Misc
            formatCurrency,
            formatDate,
            ConsultingSettingsStatus,
            ContactType,

            // Report date
            reportDateUntil,
            isReportDateInCurrentMonth,
            formattedReportDateMonth,
            formattedReportDateYearMonth,
            setReportDateToPreviousMonth,
            setReportDateToNextMonth,
            setReportDateToCurrentMonth,

            // Entity
            isLoadingEntity,
            entity,
            isLoadingEntityReports,
            entityReports,
            currentEntityReport,

            // Subentities
            subentitiesTableColumns,
            showInactiveSubentities,
            hideZeroAmountSubentities,
            isLoadingSubentities,
            isLoadingSubentityReports,
            subentities,
            subentitiesItems,
        }
    },
}
</script>

<style lang="scss" scoped>
::v-deep {
    .entity-report-amount-wrapper {
        background-color: var(--color-background-secondary);

        @media print {
            background-color: var(--color-background-primary);
        }
    }

    .subentities-wrapper {
        height: auto;
        opacity: 1;
        transition: all 0.5s $defaultTransitionTimingFunction $defaultTransitionDelay;

        &.has-no-subentities {
            height: 0;
            max-height: 0;
            opacity: 0;
        }
    }

    .amount-column {
        width: 20ch;
    }

    .actions-column {
        width: 10rem;

        @media print {
            display: none;
        }
    }
}
</style>

